/**
 * Turns the authoritative state stream into stage sound (2026-09-08): each
 * device diffs the state it just received against the one before and fires
 * the matching cue, so every screen in the room stays in sync with the game
 * rather than with a click somewhere.
 *
 * Per-device mute lives in localStorage. The beamer and the author devices
 * default to sound on; the moderation defaults to off, because it usually
 * sits on the same machine that drives the projection and would double
 * every cue.
 */

import { useEffect, useRef, useState } from 'react';
import type { Role } from '../../shared/protocol';
import type { PublicState } from '../../shared/types';
import { onAudioLockChange, playSfx, preloadSfx, unlockAudio, unlockOnGesture, audioLocked } from './sfx';

export interface SoundHandle {
  enabled: boolean;
  setEnabled: (value: boolean) => void;
  /** True while the browser still refuses to play without a gesture. */
  locked: boolean;
  unlock: () => void;
  /** Manual filler cue for purely local view changes (e.g. the ENDE screen). */
  slide: () => void;
}

function storageKey(role: Role): string {
  return `sa-sfx-${role}`;
}

export function useGameSounds(state: PublicState | null, role: Role): SoundHandle {
  const [enabled, setEnabledState] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return role !== 'moderator';
    const stored = localStorage.getItem(storageKey(role));
    return stored === null ? role !== 'moderator' : stored === 'on';
  });
  const [locked, setLocked] = useState<boolean>(() => audioLocked());

  useEffect(() => {
    preloadSfx();
    unlockOnGesture();
    return onAudioLockChange(setLocked);
  }, []);

  const setEnabled = (value: boolean) => {
    setEnabledState(value);
    try {
      localStorage.setItem(storageKey(role), value ? 'on' : 'off');
    } catch {
      /* private mode — the toggle just does not survive a reload */
    }
    if (value) unlockAudio();
  };

  const previous = useRef<PublicState | null>(null);

  useEffect(() => {
    if (!state) return;
    const before = previous.current;
    previous.current = state;
    if (!before || !enabled) return;

    const a = before.round;
    const b = state.round;
    const newRound = a.number !== b.number;

    if (newRound) {
      // A fresh round was prepared — a new slide, nothing louder to say yet.
      playSfx('slide-transition', { enabled, filler: true });
      return;
    }

    if (a.phase !== b.phase) {
      if (b.phase === 'running' && (a.phase === 'idle' || a.phase === 'intro')) {
        playSfx('round-start', { enabled });
        return;
      }
      if (b.phase === 'ended') {
        playSfx('round-end', { enabled });
        return;
      }
      // intro slide, pause, resume, back to setup: the filler cue.
      playSfx('slide-transition', { enabled, filler: true });
    }

    if (b.activeJokers.length > a.activeJokers.length) {
      playSfx('joker-activated', { enabled });
      return;
    }

    if (b.phase === 'running' && a.phase === 'running' && a.activeAuthor !== b.activeAuthor) {
      playSfx('player-switch', { enabled });
    }
  }, [state, enabled]);

  return {
    enabled,
    setEnabled,
    locked,
    unlock: unlockAudio,
    slide: () => playSfx('slide-transition', { enabled, filler: true }),
  };
}
